/**
 * API service for yolo-shop to call yolo-site-api.
 */

import { getPricingCustomerId } from '../utils/pricingContext';

const getApiBase = (): string => {
  const runtime = (globalThis as { __ENV__?: Record<string, string> }).__ENV__;
  return runtime?.VITE_YOLO_API_URL || '/api/v1';
};

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
}

export async function apiRequest<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { body, params, headers: customHeaders, ...rest } = options;

  let url = `${getApiBase()}${endpoint}`;
  if (params) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') {
        searchParams.append(key, String(value));
      }
    }
    const qs = searchParams.toString();
    if (qs) {
      url += `?${qs}`;
    }
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...customHeaders,
  };
  const customerId = getPricingCustomerId();
  if (customerId) {
    headers['x-customer-id'] = customerId;
  }

  const response = await fetch(url, {
    ...rest,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    let errorBody: unknown;
    try {
      errorBody = await response.json();
    } catch {
      // Ignore JSON parse errors
    }
    throw new ApiError(`API request failed: ${response.statusText}`, response.status, errorBody);
  }

  return response.json() as Promise<T>;
}

export const api = {
  get: <T>(endpoint: string, params?: RequestOptions['params'], signal?: AbortSignal) =>
    apiRequest<T>(endpoint, { method: 'GET', params, signal }),

  post: <T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'body'>) =>
    apiRequest<T>(endpoint, { ...options, method: 'POST', body }),
};
